import React from 'react';
import {Switch, TouchableOpacity} from 'react-native';
import Box from '@/Atoms/Box';
import CCText from '@/Atoms/CCText';
import Spacer from '@/Atoms/Spacer';
import Ionicons from 'react-native-vector-icons/Ionicons';
import CCColors, {updateColorWithOpacity} from '@/Utils/CCColors';
import WorkbookMovesList from '@/Atoms/WorkbookMovesList';
import {
  PRACTICE_DIFFICULTIES,
  type PracticeDifficulty,
} from '../../constants/practiceDifficulty';
import {PracticeGoalBanner, PracticeFeedbackCard} from '.';
import type {PracticeOutcome, PracticeVerdict} from '.';
import type {PracticeGoal} from '../../types';

/**
 * The whole student-facing practice surface, in one place.
 *
 * Both layouts (the mobile bottom-sheet tab and the desktop workspace panel)
 * used to inline their own near-copy of this: two goal banners, two feedback
 * cards, two "Your line" lists and two subtly different Reset buttons. Any
 * change to practice UX had to be made twice and the copy had already started
 * drifting. `heading` is the only thing that genuinely differed.
 */

export type PracticePanelProps = {
  /** Rendered above the cards on desktop; omitted in the compact mobile tab. */
  heading?: boolean;
  practiceOutcome: PracticeOutcome;
  practiceVerdict: PracticeVerdict;
  practiceGoal: PracticeGoal | null | undefined;
  practiceMoveCount: number;
  takebackCount: number;
  isEngineThinking: boolean;
  reachedEndOfLine: boolean;
  dismissedEndCard: boolean;
  difficulty: PracticeDifficulty;
  onChangeDifficulty: (key: string) => void;
  canTakeBack: boolean;
  onTakeBack: () => void;
  onReset: () => void;
  onAnalyseLine: () => void;
  hasNextChapter: boolean;
  onNextChapter: () => void;
  /** The attempt's own moves — never the chapter's authored tree. */
  attemptMoves: any[];
  activeMove: any;
  onSelectMove: (move: any) => void;
  setLoading: (loading: boolean) => void;
  /** Sandbox only: leave the game and put the board back where it was. An
   * Exercise has nowhere to exit to - it IS the chapter - so it omits this. */
  onExit?: () => void;
  exitLabel?: string;
  /**
   * Exercise only. Whether the student may retune the opponent the coach
   * chose. Null/undefined means 'free', which is how every chapter authored
   * before the setting existed behaves. A Sandbox never passes this - it's
   * the viewer's own game, so there is nothing to protect.
   */
  strengthPolicy?: 'fixed' | 'floor' | 'free' | null;
  /** The coach's own practice_difficulty, i.e. the floor. Null = Maximum. */
  strengthFloorElo?: number | null;
  /** Take backs permitted this attempt. Null/undefined = unlimited. */
  takebacksAllowed?: number | null;
  /**
   * Engine assistance during the game: eval bar, best-move arrows and the
   * engine's lines. Off by default because all three point straight at the
   * move you're being asked to find - but it's the viewer's own call, so it's
   * a toggle rather than a rule. Omit the handler to hide the row entirely
   * (e.g. where the engine UI isn't available to this viewer anyway).
   */
  assistEnabled?: boolean;
  onChangeAssistEnabled?: (value: boolean) => void;
};

/**
 * Elo for comparison, with null ("Maximum") treated as the strongest possible
 * setting rather than the weakest - it's an absent *cap*, not an absent level.
 */
function comparableElo(elo: number | null): number {
  return elo == null ? Number.POSITIVE_INFINITY : elo;
}

function DifficultyPicker({
  difficulty,
  onChange,
  disabled,
  strengthPolicy,
  strengthFloorElo,
}: {
  difficulty: PracticeDifficulty;
  onChange: (key: string) => void;
  disabled: boolean;
  strengthPolicy?: 'fixed' | 'floor' | 'free' | null;
  strengthFloorElo?: number | null;
}) {
  const policy = strengthPolicy ?? 'free';
  // 'fixed' pins the opponent outright; 'floor' lets the student make it
  // harder but never easier than the coach set. Either way the currently
  // selected level stays pressable so the row never looks broken.
  const isLevelLocked = (levelElo: number | null, isSelected: boolean) => {
    if (policy === 'free' || isSelected) {
      return false;
    }
    if (policy === 'fixed') {
      return true;
    }
    return comparableElo(levelElo) < comparableElo(strengthFloorElo ?? null);
  };
  const policyNote =
    policy === 'fixed'
      ? ' Your coach fixed the opponent’s strength for this exercise.'
      : policy === 'floor'
      ? ' Your coach set a minimum strength — you can make it harder, not easier.'
      : '';
  return (
    <Box>
      <CCText
        fontSize={11}
        fontWeight="bold"
        color={CCColors.Workbook.Grey}
        style={{letterSpacing: 0.5, marginBottom: 6}}>
        OPPONENT STRENGTH
      </CCText>
      <Box style={{flexDirection: 'row', flexWrap: 'wrap', gap: 6}}>
        {PRACTICE_DIFFICULTIES.map(level => {
          const isSelected = level.key === difficulty.key;
          const lockedByPolicy = isLevelLocked(level.elo, isSelected);
          const isDisabled = disabled || lockedByPolicy;
          return (
            <TouchableOpacity
              key={level.key}
              disabled={isDisabled}
              onPress={() => onChange(level.key)}
              style={{
                paddingVertical: 6,
                paddingHorizontal: 10,
                borderRadius: 14,
                borderWidth: 1,
                opacity: isDisabled ? 0.5 : 1,
                borderColor: isSelected
                  ? CCColors.Workbook.GoldText
                  : updateColorWithOpacity(CCColors.Workbook.Brown, 0.2),
                backgroundColor: isSelected
                  ? CCColors.LightYellow
                  : 'transparent',
              }}>
              <CCText
                fontSize={11.5}
                fontWeight={isSelected ? 'bold' : 'normal'}
                color={
                  isSelected
                    ? CCColors.Workbook.GoldText
                    : CCColors.Workbook.Brown
                }>
                {level.label}
              </CCText>
            </TouchableOpacity>
          );
        })}
      </Box>
      <Spacer spacing={4} />
      <CCText fontSize={11} color={CCColors.Workbook.Grey}>
        {difficulty.desc}
        {disabled ? ' — finish or reset the game to change this.' : ''}
        {policyNote}
      </CCText>
    </Box>
  );
}

export default function PracticePanel({
  heading = false,
  practiceOutcome,
  practiceVerdict,
  practiceGoal,
  practiceMoveCount,
  takebackCount,
  isEngineThinking,
  reachedEndOfLine,
  dismissedEndCard,
  difficulty,
  onChangeDifficulty,
  canTakeBack,
  onTakeBack,
  onReset,
  onAnalyseLine,
  hasNextChapter,
  onNextChapter,
  attemptMoves,
  activeMove,
  onSelectMove,
  setLoading,
  onExit,
  exitLabel,
  strengthPolicy,
  strengthFloorElo,
  takebacksAllowed,
  assistEnabled,
  onChangeAssistEnabled,
}: PracticePanelProps) {
  const isOngoing = practiceOutcome.status === 'ongoing';
  // Changing strength mid-game would silently rewrite the opponent the
  // student has been playing, so it's only offered before the first move or
  // once the game is over.
  const canChangeDifficulty = attemptMoves.length === 0 || !isOngoing;
  // Take backs are part of an assessed result, so a coach can bound them.
  // Undefined/null means unlimited - the behaviour before the setting existed.
  const takebacksExhausted =
    takebacksAllowed != null && takebackCount >= takebacksAllowed;
  const takebacksRemaining =
    takebacksAllowed == null
      ? null
      : Math.max(0, takebacksAllowed - takebackCount);
  const takeBackEnabled = canTakeBack && !takebacksExhausted;

  return (
    <Box style={{padding: 16}}>
      {heading && (
        <>
          <Box style={{alignItems: 'center', marginVertical: 20}}>
            <Ionicons
              name="hardware-chip-outline"
              size={48}
              color={CCColors.Workbook.GoldText}
            />
            <Spacer spacing={10} />
            <CCText
              fontSize={18}
              fontWeight="bold"
              color={CCColors.TextColor.Primary}>
              Practice with Computer
            </CCText>
            <CCText
              fontSize={13}
              color={CCColors.Workbook.Brown}
              style={{textAlign: 'center', marginTop: 6}}>
              Play out this position against the engine.
            </CCText>
          </Box>
          <Spacer spacing={10} />
        </>
      )}

      <PracticeGoalBanner
        practiceOutcome={practiceOutcome}
        practiceGoal={practiceGoal}
        practiceMoveCount={practiceMoveCount}
        isEngineThinking={isEngineThinking}
        difficultyLabel={difficulty.label}
        takebackCount={takebackCount}
      />
      <PracticeFeedbackCard
        practiceOutcome={practiceOutcome}
        practiceVerdict={practiceVerdict}
        practiceGoal={practiceGoal}
        dismissedEndCard={dismissedEndCard}
        reachedEndOfLine={reachedEndOfLine}
        onRetry={onReset}
        onAnalyseLine={onAnalyseLine}
        hasNextChapter={hasNextChapter}
        onNextChapter={onNextChapter}
      />

      <Spacer spacing={16} />
      <DifficultyPicker
        difficulty={difficulty}
        onChange={onChangeDifficulty}
        disabled={!canChangeDifficulty}
        strengthPolicy={strengthPolicy}
        strengthFloorElo={strengthFloorElo}
      />

      {!!onChangeAssistEnabled && (
        <>
          <Spacer spacing={14} />
          <Box
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 8,
            }}>
            <Box style={{flex: 1}}>
              <CCText fontSize={12} color={CCColors.Workbook.Brown}>
                Engine assistance
              </CCText>
              <CCText fontSize={10.5} color={CCColors.Workbook.Grey}>
                {assistEnabled
                  ? 'Eval bar, arrows and engine lines are showing.'
                  : 'Eval bar, arrows and engine lines stay hidden while you play.'}
              </CCText>
            </Box>
            <Switch
              trackColor={{
                false: updateColorWithOpacity(CCColors.Workbook.Brown, 0.16),
                true: CCColors.Green,
              }}
              thumbColor={CCColors.White}
              value={!!assistEnabled}
              onValueChange={onChangeAssistEnabled}
            />
          </Box>
        </>
      )}

      <Spacer spacing={16} />
      <Box style={{flexDirection: 'row', gap: 8}}>
        {/* A coach who allowed zero take backs gets no button at all, rather
            than a permanently dead one promising "0 left". */}
        {isOngoing && takebacksAllowed !== 0 && (
          <TouchableOpacity
            disabled={!takeBackEnabled}
            onPress={onTakeBack}
            style={{
              flex: 1,
              padding: 12,
              borderRadius: 8,
              borderWidth: 1,
              borderColor: CCColors.Workbook.Brown,
              alignItems: 'center',
              justifyContent: 'center',
              opacity: takeBackEnabled ? 1 : 0.4,
            }}>
            <CCText color={CCColors.Workbook.Brown} fontWeight="bold">
              {takebacksRemaining == null
                ? 'Take back'
                : `Take back (${takebacksRemaining} left)`}
            </CCText>
          </TouchableOpacity>
        )}
        <TouchableOpacity
          style={{
            flex: 1,
            backgroundColor: CCColors.Primary.Brown,
            padding: 12,
            borderRadius: 8,
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onPress={onReset}>
          <CCText color={CCColors.White} fontWeight="bold">
            {isOngoing ? 'Restart' : 'Play again'}
          </CCText>
        </TouchableOpacity>
      </Box>

      {!!onExit && (
        <>
          <Spacer spacing={8} />
          <TouchableOpacity
            onPress={onExit}
            style={{
              paddingVertical: 10,
              borderRadius: 8,
              alignItems: 'center',
              justifyContent: 'center',
            }}>
            <CCText color={CCColors.Workbook.Grey} fontWeight="bold">
              {exitLabel || 'Exit'}
            </CCText>
          </TouchableOpacity>
        </>
      )}

      {attemptMoves.length > 0 && (
        <>
          <Spacer spacing={20} />
          <CCText
            fontSize={12}
            fontWeight="bold"
            color={CCColors.Workbook.Brown}>
            Your line
          </CCText>
          <Spacer spacing={10} />
          <WorkbookMovesList
            gameMoves={attemptMoves}
            activeMove={activeMove}
            setmoveSelected={onSelectMove}
            setLoading={setLoading}
            isOwner={false}
          />
        </>
      )}
    </Box>
  );
}
